import { analytics } from "~/actions/analytics";
import type { Action, ActionContext } from "~/actions/types";

/**
 * Wraps `perform` so every invocation is recorded with the surface that triggered it, and
 * pushed onto the shell's recents list. Declaring an action is therefore the only thing an
 * author has to do — instrumentation and recency come with it.
 */
export function createAction(definition: Omit<Action, "id"> & { id?: string }): Action {
  const id = definition.id ?? crypto.randomUUID();
  const analyticsName = definition.analyticsName ?? id;
  const { perform } = definition;

  return {
    ...definition,
    id,
    analyticsName,
    perform: perform
      ? (context: ActionContext) => {
          analytics.trackAction(analyticsName, context.source);
          context.shell.getState().rememberAction(id);
          return perform(context);
        }
      : undefined,
  };
}

const registered: Action[] = [];

/** Every action the app knows about, in declaration order. */
export const rootActions: readonly Action[] = registered;

export function registerActions(...actions: Action[]): void {
  for (const action of actions) {
    const existing = registered.findIndex((a) => a.id === action.id);
    if (existing >= 0) {
      // Hot reload re-runs definition modules; replace rather than duplicate.
      registered[existing] = action;
    } else {
      registered.push(action);
    }
  }
}

export function clearActions(): void {
  registered.length = 0;
}

export function findAction(
  id: string,
  actions: readonly Action[] = registered,
): Action | undefined {
  return actions.find((action) => action.id === id);
}
